import { List } from '@almbrand/list';
import { LoadingSpinner } from '@almbrand/loadingspinner';
import classNames from 'classnames';
import { PageContext } from 'context/PageContext';
import { useModal } from 'hooks';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { formatNumber } from '../../../../utilities/format';
import styles from './PackageTile.module.scss';
import { SelectPackageButton } from './SelectPackageButton';

export interface PackageTileProps {
	header: {
		title: string;
		discountText?: string;
	};
	priceBase: number;
	priceDiscount: number;
	hasCalculationError?: boolean;
	noSelectionError?: boolean;
	errorMessage?: string;
	coverages: CoverGroup[];
	isLoading?: boolean;
	getIsValid?: (isValid: boolean) => void;
	setActiveItem?: (active: number) => void;
	allCalculationsDone: boolean;
	index: number;
}

export const PackageTile: React.FC<PackageTileProps> = ({
	header,
	isLoading = false,
	getIsValid,
	setActiveItem,
	coverages,
	priceBase,
	priceDiscount,
	hasCalculationError,
	noSelectionError,
	errorMessage,
	allCalculationsDone,
	index,
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};

	const [selected, setSelected] = useState<boolean>(false);
	const listRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

	const form = useAppSelector((state) => state.calculatorForm);
	const { displayModal } = useModal();

	const pricePostfix = ' kr./md.';

	const checkIfTextWraps = (coverageName: string) => {
		const element = listRefs.current[coverageName];
		if (!element) return false;

		const svg = element.getElementsByTagName('svg')[0];
		const svgHeight = svg?.clientHeight ?? 0;

		return element.scrollHeight > svgHeight;
	};

	const mapItems = () =>
		coverages.map((coverage, index) => (
			<React.Fragment key={index + (coverage.coverName ?? '')}>
				{coverage?.coverName && (
					<button
						onClick={() => modalCoverage(coverage)}
						key={coverage.coverName}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								modalCoverage(coverage);
							}
						}}
						className={classNames(
							styles.PackageTile_coverages_button,
							checkIfTextWraps(coverage.coverName) && styles['PackageTile_coverages_button--marginBottom']
						)}
					>
						<div ref={(el) => (listRefs.current[coverage.coverName] = el)}>
							<List
								items={[
									{
										iconProp: {
											themeName: theme,
											icon: coverage.isActive ? 'check_thin' : 'horizontal_line',
										},
										text: coverage.coverName,
										id: index,
										...(coverage.isActive ? {} : { className: styles.notActive }),
									},
								]}
							/>
						</div>
					</button>
				)}
			</React.Fragment>
		));

	useEffect(() => {
		const id = form.SELECTED_PACKAGE?.data?.id;
		setSelected(id === header.title);
		if (id === header.title) {
			setActiveItem(index);
		}
	}, [form.SELECTED_PACKAGE]);

	const modalCoverage = (coverage: CoverGroup) => {
		if (coverage.coverName && coverage.coverDescription?.content) {
			const { title, subtitle, content } = coverage.coverDescription ?? {};
			displayModal({ title, subtitle, content });
		}
	};

	const getPrimaryPriceArea = () => {
		if (hasCalculationError) {
			return '-';
		}
		if (isLoading) {
			return (
				<LoadingSpinner
					style='small'
					type='dotted'
					color='primary-5'
				/>
			);
		}

		if (priceDiscount !== undefined && priceDiscount > 0) {
			return formatNumber(priceDiscount) + pricePostfix;
		} else {
			return formatNumber(priceBase) + pricePostfix;
		}
	};

	const getSecondaryPriceArea = () => {
		if (hasCalculationError || priceDiscount === undefined || priceDiscount <= 0 || !header.discountText) {
			return null;
		}

		return (
			<div className={styles.PackageTile_header_discount}>
				{header.discountText}
				<span>&nbsp;</span>
				<s>{formatNumber(priceBase) + pricePostfix}</s>
			</div>
		);
	};

	return (
		<div className={styles.PackageTile}>
			<div className={styles.PackageTile_header}>
				<h3 className={styles.PackageTile_header_title}>{header.title}</h3>
				<h1 className={styles.PackageTile_header_price}>{getPrimaryPriceArea()}</h1>
				{getSecondaryPriceArea()}
			</div>
			<div className={styles.PackageTile_coverages}>{mapItems()}</div>
			<SelectPackageButton
				header={header}
				setSelected={setSelected}
				isLoading={isLoading}
				isSelected={selected}
				priceDiscount={priceDiscount}
				priceBase={priceBase}
				hasCalculationError={hasCalculationError}
				noSelectionError={noSelectionError}
				index={index}
				getIsValid={getIsValid}
				coverages={coverages}
				allCalculationsDone={allCalculationsDone}
				errorMessage={errorMessage}
			/>
		</div>
	);
};
